import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { trackFeatureUsage } from '../services/api';
import axios from 'axios';
import { 
  HelpCircle,
  Search,
  Book,
  ChevronRight,
  ChevronDown,
  Rocket,
  Users,
  ClipboardList,
  UserCheck,
  FileText,
  CreditCard,
  MessageCircle,
  ExternalLink
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  'rocket': Rocket,
  'users': Users,
  'clipboard': ClipboardList,
  'user-check': UserCheck,
  'file-text': FileText,
  'credit-card': CreditCard,
};

const HelpPage = () => {
  const [articles, setArticles] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [activeTab, setActiveTab] = useState('guides');

  const loadHelpContent = useCallback(async () => {
    try {
      const [articlesRes, faqsRes] = await Promise.all([
        axios.get(`${API}/api/help/articles`),
        axios.get(`${API}/api/help/faqs`)
      ]);
      setArticles(articlesRes.data);
      setFaqs(faqsRes.data);
    } catch (error) {
      console.error('Failed to load help content');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHelpContent();
    trackFeatureUsage('help', 'view').catch(() => {});
  }, [loadHelpContent]);

  const filteredArticles = articles.filter(article =>
    article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.summary.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const categories = [...new Set(articles.map(a => a.category))];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="help-page">
        {/* Header */}
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-red-500 font-body mb-1">Support</p>
          <h1 className="font-heading text-4xl md:text-5xl text-white tracking-tight">
            HELP CENTER
          </h1>
          <p className="text-zinc-500 mt-2 font-body">Find answers and learn how to use Black Arrow MMA</p>
        </div>

        {/* Search */}
        <div className="relative max-w-xl">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <Input
            placeholder="Search help articles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 h-12 bg-ba-surface border-ba-border text-white"
            data-testid="help-search-input"
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-ba-border pb-4">
          <Button
            variant={activeTab === 'guides' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('guides')}
            className={cn(
              "font-heading uppercase tracking-wider",
              activeTab === 'guides' ? 'bg-red-600 text-white' : 'text-zinc-400'
            )}
          >
            <Book className="w-4 h-4 mr-2" />
            Guides
          </Button>
          <Button
            variant={activeTab === 'faq' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('faq')}
            className={cn(
              "font-heading uppercase tracking-wider",
              activeTab === 'faq' ? 'bg-red-600 text-white' : 'text-zinc-400'
            )}
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            FAQ
          </Button>
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'guides' && (
            <motion.div
              key="guides"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {selectedArticle ? (
                /* Article Detail View */
                <div className="space-y-6">
                  <Button
                    variant="ghost"
                    onClick={() => setSelectedArticle(null)}
                    className="text-zinc-400 hover:text-white"
                  >
                    ← Back to articles
                  </Button>
                  
                  <div className="bg-ba-surface border border-ba-border rounded-sm p-6 md:p-8">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="px-3 py-1 bg-red-600/20 text-red-500 text-xs uppercase tracking-wider rounded-sm">
                        {selectedArticle.category}
                      </span>
                    </div>
                    <h2 className="font-heading text-3xl text-white mb-4">{selectedArticle.title}</h2>
                    <div 
                      className="prose prose-invert prose-zinc max-w-none
                        prose-headings:font-heading prose-headings:text-white prose-headings:tracking-tight
                        prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                        prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                        prose-p:text-zinc-400 prose-p:font-body
                        prose-strong:text-white
                        prose-ul:text-zinc-400
                        prose-ol:text-zinc-400
                        prose-li:marker:text-red-500"
                      dangerouslySetInnerHTML={{ 
                        __html: selectedArticle.content
                          .replace(/^## /gm, '<h2>')
                          .replace(/^### /gm, '<h3>')
                          .replace(/\n\n/g, '</p><p>')
                          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                          .replace(/^- /gm, '<li>')
                          .replace(/^(\d+)\. /gm, '<li>')
                      }}
                    />
                  </div>
                </div>
              ) : (
                /* Article List View */
                <div className="space-y-8">
                  {categories.map(category => {
                    const categoryArticles = filteredArticles.filter(a => a.category === category);
                    if (categoryArticles.length === 0) return null;
                    
                    return (
                      <div key={category}>
                        <h3 className="font-heading text-lg text-zinc-400 uppercase tracking-wider mb-4">
                          {category}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {categoryArticles.map(article => {
                            const IconComponent = iconMap[article.icon] || HelpCircle;
                            return (
                              <button
                                key={article.id}
                                onClick={() => {
                                  setSelectedArticle(article);
                                  trackFeatureUsage('help', 'read_article', { article: article.id }).catch(() => {});
                                }}
                                className="bg-ba-surface border border-ba-border p-5 rounded-sm text-left hover:border-red-600/50 transition-all group"
                                data-testid={`help-article-${article.id}`}
                              >
                                <div className="flex items-start gap-4">
                                  <div className="w-10 h-10 bg-red-600/10 border border-red-600/20 rounded-sm flex items-center justify-center flex-shrink-0">
                                    <IconComponent className="w-5 h-5 text-red-500" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-heading text-lg text-white group-hover:text-red-500 transition-colors">
                                      {article.title}
                                    </h4>
                                    <p className="text-zinc-500 text-sm mt-1 line-clamp-2">
                                      {article.summary}
                                    </p>
                                  </div>
                                  <ChevronRight className="w-5 h-5 text-zinc-600 group-hover:text-red-500 transition-colors flex-shrink-0" />
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  
                  {filteredArticles.length === 0 && (
                    <div className="text-center py-12">
                      <HelpCircle className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                      <p className="text-zinc-500">No articles found matching "{searchQuery}"</p>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'faq' && (
            <motion.div
              key="faq"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-3"
            >
              {filteredFaqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-ba-surface border border-ba-border rounded-sm overflow-hidden"
                >
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                    className="w-full p-5 flex items-center justify-between text-left hover:bg-zinc-900/50 transition-colors"
                    data-testid={`faq-item-${index}`}
                  >
                    <span className="font-body text-white pr-4">{faq.question}</span>
                    <ChevronDown className={cn(
                      "w-5 h-5 text-zinc-500 transition-transform flex-shrink-0",
                      expandedFaq === index && "rotate-180"
                    )} />
                  </button>
                  <AnimatePresence>
                    {expandedFaq === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="px-5 pb-5 text-zinc-400 font-body border-t border-ba-border pt-4">
                          {faq.answer}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
              
              {filteredFaqs.length === 0 && (
                <div className="text-center py-12">
                  <MessageCircle className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-500">No FAQs found matching "{searchQuery}"</p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Contact Support */}
        <div className="bg-ba-surface border border-ba-border rounded-sm p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4 text-center sm:text-left">
            <div className="w-12 h-12 bg-red-600/10 border border-red-600/20 rounded-full flex items-center justify-center">
              <HelpCircle className="w-6 h-6 text-red-500" />
            </div>
            <div>
              <h3 className="font-heading text-lg text-white">Still need help?</h3>
              <p className="text-zinc-500 text-sm">Contact our support team for assistance</p>
            </div>
          </div>
          <Button
            className="bg-red-600 hover:bg-red-700 text-white font-heading uppercase tracking-wider"
            onClick={() => window.open('mailto:support@blackarrowmma.com', '_blank')}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Contact Support
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default HelpPage;
